import { isPrivateEvent } from './calendarPrivacy.js'

// Bounded, shared conversation projection. Card evidence is context, never authority.
export const HISTORY_LIMIT = 40
export const HISTORY_BUDGET = 48000
export function conversationHistory(messages, { voiceMode = false } = {}) {
  const rows = (Array.isArray(messages) ? messages : []).filter(m =>
    ['user', 'assistant'].includes(m?.role) && typeof m.content === 'string' && !m.streaming)
  let budget = HISTORY_BUDGET
  return rows.slice(-HISTORY_LIMIT).reverse().map(m => {
    const privateVoice = voiceMode && m.personalMail
    let content = privateVoice ? '(Personal information unavailable on a spoken turn.)' : m.content.slice(0, m.calendarPayload || m.calendarResult || m.resolvedContacts?.length ? 4000 : 14100)
    if (!privateVoice && m.role === 'assistant') {
      const evidence = m.calendarPayload?.events?.length
        ? { state: 'proposed; requires approval, not created', proposal: m.calendarPayload }
        : m.calendarResult ? { state: 'calendar endpoint result reported by client', result: m.calendarResult } : null
      if (m.resolvedContacts?.length) content += '\n[Resolved contacts: ' + JSON.stringify(m.resolvedContacts.slice(0, 4)).slice(0, 2000) + ']'
      if (evidence) content += '\n[Calendar evidence: ' + JSON.stringify(evidence).slice(0, 8000) + ']'
    }
    if (content.length > budget) return null
    budget -= content.length
    return { role: m.role, content, personalMail: !!m.personalMail }
  }).filter(Boolean).reverse()
}

export function validCalendarPayload(payload) {
  return Array.isArray(payload?.events) && payload.events.length > 0 && payload.events.length <= 10 &&
    payload.events.every(e => typeof e.title === 'string' && e.title.trim() &&
      typeof e.start_datetime === 'string' && /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(e.start_datetime) &&
      Number.isFinite(Date.parse(e.start_datetime)) &&
      (e.end_datetime == null || (typeof e.end_datetime === 'string' && Number.isFinite(Date.parse(e.end_datetime)) && Date.parse(e.end_datetime) > Date.parse(e.start_datetime))) &&
      (e.attendees == null || (Array.isArray(e.attendees) && e.attendees.every(a => typeof a === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a)))))
}

// Floating proposal times use the chat/calendar contract's Pacific timezone.
function calendarMinute(value) {
  if (typeof value !== 'string') return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) return value.slice(0,16)
  if (!Number.isFinite(Date.parse(value))) return null
  return new Intl.DateTimeFormat('sv-SE', { timeZone:'America/Los_Angeles', year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hourCycle:'h23' }).format(new Date(value)).replace(' ', 'T')
}
// A bounded read must never represent an incomplete page as absence evidence.
export function calendarSnapshot(data, start, end) {
  if (!Array.isArray(data?.items) || data.nextPageToken) return null
  const events = data.items.filter(e => e.status !== 'cancelled').map(e => ({
    id: e.id, organizer: e.organizer || null,
    responseStatus: (e.attendees || []).find(a => a.self)?.responseStatus || null,
    attendeeEmails: (e.attendees || []).map(a => a.email).filter(Boolean),
    location: e.location || null, title: e.summary || '(no title)',
    start: e.start?.dateTime || e.start?.date || '',
    end: e.end?.dateTime || e.end?.date || '',
    allDay: !!e.start?.date && !e.start?.dateTime,
    // Private in Google: carried through so a spoken surface can redact it
    // (calendarPrivacy.js; buildUserContent's spokenAudience gate).
    private: isPrivateEvent(e),
  }))
  events.coverage = { start: calendarMinute(start), end: calendarMinute(end) }
  return events
}
export function existingCalendarMatch(proposal, events = []) {
  return events.find(existing => {
    if (existing.status === 'cancelled' || !calendarMinute(existing.start)) return false
    if (calendarMinute(existing.start) !== calendarMinute(proposal.start_datetime)) return false
    const sameTitle = String(existing.title || '').trim().toLowerCase() === String(proposal.title || '').trim().toLowerCase()
    const participants = [...(existing.attendeeEmails || []), existing.organizer?.email || existing.organizer || ''].filter(v => typeof v === 'string').map(v => v.toLowerCase())
    return sameTitle || (proposal.attendees || []).some(email => participants.includes(email.toLowerCase()))
  })
}
export function needsFreshCalendar(message, history = []) {
  return /\b(calendar|schedule|scheduling|invite|meeting|lunch|dinner|appointment|book|block time)\b/i.test([message, ...history.map(m => m.content || '')].join(' '))
}

// Never salvage an action's announcement from an incomplete structured response.
export function validateCalendarResponse(parsed, { complete = true, calendarEvents } = {}) {
  if (!complete) return { type: 'answer', content: "I couldn't finish preparing that response. No new calendar card or event was created. Please try again." }
  if (parsed?.type === 'propose_calendar_events' || parsed?.calendarPayload) {
    if (!validCalendarPayload(parsed.calendarPayload)) return { ...parsed, type: 'answer', calendarPayload: null,
      content: "I couldn't prepare a complete calendar card. No event was created. Please try again." }
    if (calendarEvents === null) return { ...parsed, type: 'answer', calendarPayload: null,
      content: "I couldn't check the current calendar for existing invitations. Please retry before creating a new event." }
    const existing = parsed.calendarPayload.events.map(event => existingCalendarMatch(event, calendarEvents || [])).find(Boolean)
    if (existing) return { ...parsed, type:'answer', calendarPayload:null,
      content: `A matching event is already on the calendar${existing.organizer?.email ? ` from ${existing.organizer.email}` : ''}${existing.responseStatus ? ` (your RSVP: ${existing.responseStatus})` : ''}. I haven't created an event or changed your RSVP. Review the existing invitation before making another event.` }
    if (calendarEvents !== undefined && parsed.calendarPayload.events.some(event => {
      const minute = calendarMinute(event.start_datetime)
      return !calendarEvents?.coverage?.start || !calendarEvents?.coverage?.end ||
        minute <= calendarEvents.coverage.start || minute >= calendarEvents.coverage.end
    })) return { ...parsed, type:'answer', calendarPayload:null,
      content: "I couldn't verify existing invitations for that date in the current calendar window. Please check that date before creating another event." }
    return { ...parsed, content: 'Ready for your review — tap Add to Calendar to create the event' + (parsed.calendarPayload.events.length > 1 ? 's.' : '.'),
      calendarPayload: { ...parsed.calendarPayload, proposalId: parsed.calendarPayload.proposalId || crypto.randomUUID() } }
  }
  const content = parsed?.content || ''
  const currentActionClaim = /\bI(?:'ve| have)?\s+(?:just\s+)?(?:proposed|added|created|scheduled|booked)\b/i.test(content)
    || /^(?:Done[!.,— -]*|Successfully\s+)?(?:Added|Created|Scheduled|Booked)\b/i.test(content)
    || /\b(?:here(?:'s| is)|I(?:'ve| have)? prepared)\b.{0,50}\bcalendar card\b/i.test(content)
    || /\btap ['"]?Add to Calendar\b/i.test(content)
  // Free-form prose has no structured event identity to match. Calendar presence
  // alone cannot authorize completion claims; matched proposals are handled above.
  const unsupportedPassiveClaim = /^Your (?:lunch|meeting|event)\b.{0,50}\b(?:is|was|has been) (?:scheduled|added|created|booked)\b/i.test(content)
  if ((currentActionClaim || unsupportedPassiveClaim) && /calendar|event|meeting|lunch/i.test(content)) {
    return { ...parsed, content: "I haven't prepared a new calendar card or created an event in this response. I can prepare the proposal using the details above." }
  }
  return parsed
}
